import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { computed, onScopeDispose, reactive, type MaybeRef } from "vue";

import type { components as OpenAPI } from "@/autogen/openapi";
import { IS_DEMO } from "@/lib/environment";
import { convertUrl } from "@/lib/images";
import { ok } from "@/lib/openapi";
import { throttle } from "@/lib/timing";

import api from "../../base";
import { useGenericListingQuery } from "../generic";
import { useNewsletterId, useXsrfHeaders } from "../utils";

type ImagePage = OpenAPI["schemas"]["ImagePage"];

type Image = OpenAPI["schemas"]["Image"];

export type ImageWithFilename = Image & { filename: string };

interface PendingImageBase {
  id: string;
  file: File;
  previewUrl: string;
}

interface PendingImageUploading extends PendingImageBase {
  status: "uploading";
  progress: number;
}

interface PendingImageFailed extends PendingImageBase {
  status: "failed";
  error: string;
}

export type PendingImage = PendingImageUploading | PendingImageFailed;

export const useImagesListingQuery = ({
  enabled = true,
}: {
  enabled?: MaybeRef<boolean>;
}) => {
  const newsletterId = useNewsletterId();

  const query = useGenericListingQuery({
    queryKey: [newsletterId, "images", "list"],
    enabled: enabled,
    async fetch({ page, signal }): Promise<ImagePage> {
      const data = await ok(
        api.get("/images", {
          signal,
          params: {
            query: {
              page: page,
            },
          },
        })
      );

      return data;
    },
  });

  return query;
};

export const useUploadImageMutation = ({
  keepFailedImages = false,
}: { keepFailedImages?: boolean } = {}) => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();
  const headers = useXsrfHeaders();

  const map = reactive(new Map<File, PendingImage>());

  const controller = new AbortController();
  onScopeDispose(() => {
    controller.abort();

    for (const [, image] of map) {
      URL.revokeObjectURL(image.previewUrl);
    }
  });

  const mutation = useMutation({
    onMutate(file: File) {
      const existing = map.get(file);
      if (existing) {
        map.set(file, {
          id: existing.id,
          file: file,
          previewUrl: existing.previewUrl,
          status: "uploading",
          progress: 0,
        });
        return;
      }

      const image = reactive<PendingImageUploading>({
        id: crypto.randomUUID(),
        file: file,
        previewUrl: URL.createObjectURL(file),
        status: "uploading",
        progress: 0,
      });

      prefetchImage(image.previewUrl);

      map.set(file, image);
    },
    async mutationFn(file: File): Promise<ImageWithFilename> {
      const image = map.get(file)! as PendingImageUploading;
      if (IS_DEMO) {
        throw new Error("Image uploads are disabled on the demo site.");
      }

      return new Promise<ImageWithFilename>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();

        formData.set("image", file, file.name);

        const handleAbort = () => {
          xhr.abort();
        };

        xhr.upload.onprogress = throttle(
          (event) => {
            if (event.lengthComputable) {
              const progress = event.loaded / event.total;
              image.progress = progress;
            }
          },
          750,
          false
        );

        xhr.onload = () => {
          controller.signal.removeEventListener("abort", handleAbort);

          let data;
          try {
            data = JSON.parse(xhr.responseText);
          } catch (error) {
            console.error(error);

            // If JSON parsing fails, use a generic error
            reject(new Error(`Failed to parse server response`));
            return;
          }

          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({
              ...data,
              filename: file.name,
            });
          } else {
            reject(
              new Error(
                data?.detail || `Upload failed with status ${xhr.status}`
              )
            );
          }
        };

        xhr.onerror = () => {
          controller.signal.removeEventListener("abort", handleAbort);

          reject(new Error("Upload failed"));
        };

        xhr.open("POST", "/v1/images");

        for (const [key, value] of Object.entries(headers.value)) {
          xhr.setRequestHeader(key, value);
        }

        controller.signal.addEventListener("abort", handleAbort);
        xhr.send(formData);
      });
    },
    async onSuccess(result, file) {
      const image = map.get(file)! as PendingImageUploading;

      await prefetchImage(convertUrl(result.image, "optimized"));

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "images", "list"],
      });

      URL.revokeObjectURL(image.previewUrl);
      map.delete(file);
    },
    onError(error, file) {
      if (!keepFailedImages) {
        map.delete(file);

        return;
      }

      const image = map.get(file)! as PendingImageUploading;

      map.set(file, {
        id: image.id,
        file: file,
        previewUrl: image.previewUrl,
        status: "failed",
        error: error instanceof Error ? error.message : "Upload failed",
      });
    },
  });

  const dismissPendingImage = (file: File) => {
    const image = map.get(file);
    if (image && image.status === "failed") {
      URL.revokeObjectURL(image.previewUrl);
      map.delete(file);
    }
  };

  return {
    ...mutation,
    pending: computed(() => [...map.values()]),
    dismissPendingImage,
  };
};

const prefetchImage = (url: string): Promise<void> => {
  return new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
};
